import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const LAST_SEEN_KEY = 'nazandoo_messages_last_seen';

/**
 * Hook that returns the count of unread messages for the current user.
 * Uses localStorage to track "last seen" timestamp — works even without is_read column.
 * Subscribes to realtime INSERT events to update the count live.
 */
export const useUnreadMessages = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const getLastSeen = useCallback((): string => {
    if (!user?.id) return new Date().toISOString();
    const stored = localStorage.getItem(`${LAST_SEEN_KEY}_${user.id}`);
    // Default: 24h ago if never set
    return stored || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  }, [user?.id]);

  /** Call this when user opens the Messages tab to reset the counter */
  const markAsSeen = useCallback(() => {
    if (!user?.id) return;
    localStorage.setItem(`${LAST_SEEN_KEY}_${user.id}`, new Date().toISOString());
    setUnreadCount(0);
  }, [user?.id]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;

    try {
      const lastSeen = getLastSeen();

      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .gt('created_at', lastSeen);

      if (!error && count !== null) {
        setUnreadCount(count);
      }
    } catch {
      // Silent fail
    }
  }, [user?.id, getLastSeen]);

  useEffect(() => {
    if (!user?.id) return;

    fetchUnreadCount();

    // Subscribe to new messages arriving for this user
    const channel = supabase
      .channel(`unread-messages-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          setUnreadCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchUnreadCount]);

  return { unreadCount, markAsSeen, refetch: fetchUnreadCount };
};
